import {
	Button,
	ChipSelect,
	Label,
	Modal,
	ModalContent,
	ModalTrigger,
	PopoverItem,
	PopoverSection,
	ResourceOptions,
} from "@ondit/ui";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useAccess } from "~/core/access/index";
import type { ResearchModel } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { ModalHeader } from "~/core/ui/dialog";
import { Hint } from "~/core/ui/page";
import { Async, type Reading } from "~/core/ui/states";

export function ModelPicker({
	value,
	onChange,
	defaultModel,
	catalogue,
	disabled = false,
}: {
	value: string;
	onChange: (value: string) => void;
	defaultModel: string;
	catalogue: Reading<{ models: ResearchModel[]; default: string }>;
	disabled?: boolean;
}) {
	const t = useT();
	const { editor } = useAccess();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [provider, setProvider] = useState("all");
	const models = catalogue.data?.models ?? [];
	const providers = [...new Set(models.map((model) => model.provider))].sort();
	const modelName = (id: string) => models.find((model) => model.model === id)?.display_name ?? id;
	const defaultLabel = t("settings.researchModelDefault", { model: modelName(defaultModel) });
	const selectedLabel = value ? modelName(value) : defaultLabel;
	const needle = query.trim().toLowerCase();
	const matches = (model: ResearchModel) =>
		model.display_name.toLowerCase().includes(needle) || model.model.toLowerCase().includes(needle);
	const shown = models.filter(
		(model) => (provider === "all" || provider === `provider:${model.provider}`) && matches(model),
	);
	const showDefault =
		provider === "all" && (defaultLabel.toLowerCase().includes(needle) || defaultModel.toLowerCase().includes(needle));
	const blocked = disabled || !editor;

	function choose(model: string) {
		if (blocked) return;
		onChange(model);
		setOpen(false);
	}

	return (
		<div className="field">
			<div className="title-row">
				<Label htmlFor="research-model">{t("settings.researchModel")}</Label>
				<Hint text={t("settings.researchModelHint")} />
			</div>
			<Modal
				open={open && !blocked}
				onOpenChange={(next) => {
					setOpen(next);
					if (next) {
						setQuery("");
						setProvider("all");
					}
				}}
			>
				<div className="flex min-w-0 items-center gap-(--s-4)">
					<span className="min-w-0 break-words">{selectedLabel}</span>
					<ModalTrigger asChild>
						<Button
							id="research-model"
							type="button"
							variant="ghost"
							size="icon"
							className="shrink-0"
							disabled={blocked}
							aria-label={t("settings.researchModelChoose")}
							title={t("settings.researchModelChoose")}
						>
							<ChevronsUpDown aria-hidden />
						</Button>
					</ModalTrigger>
				</div>
				<ModalContent
					aria-describedby={undefined}
					className="flex h-[min(var(--model-picker-height),calc(100dvh-var(--s-12)))] w-[min(var(--model-picker-width),calc(100vw-var(--s-12)))] flex-col gap-(--s-8) overflow-hidden p-(--s-10)"
				>
					<ModalHeader>{t("settings.researchModelChoose")}</ModalHeader>
					<Async state={catalogue}>
						{() => (
							<>
								<ResourceOptions
									translate={t}
									className="analytics-table-tools"
									search={{
										value: query,
										onChange: setQuery,
										placeholder: t("settings.researchModelSearch"),
									}}
									filter={{
										active: provider !== "all",
										content: (
											<div className="analytics-filter-fields">
												<ChipSelect
													value={provider}
													onChange={setProvider}
													aria-label={t("settings.researchModelProvider")}
													className="w-full"
													options={[
														{ value: "all", label: t("settings.researchModelProviderAll") },
														...providers.map((name) => ({
															value: `provider:${name}`,
															label: name || t("settings.researchModelOther"),
														})),
													]}
												/>
											</div>
										),
									}}
								/>
								{/* The list is the dialog's own scroller, not a popover's: the
								    picker is a screen of models, and a nested scroll area inside
								    a scrolling dialog gives it two. */}
								<div className="min-h-0 flex-1 overflow-y-auto">
									{shown.length === 0 && !showDefault ? (
										<p className="py-6 text-center text-sm text-[var(--text-muted)]">
											{t("settings.researchModelNone")}
										</p>
									) : (
										<>
											{showDefault && (
												<PopoverItem active={!value} onClick={() => choose("")}>
													<span className="flex-1">{defaultLabel}</span>
													{!value && <Check aria-hidden />}
												</PopoverItem>
											)}
											{providers.map((name) => {
												const group = shown.filter((model) => model.provider === name);
												return (
													group.length > 0 && (
														<div key={name}>
															<PopoverSection>{name || t("settings.researchModelOther")}</PopoverSection>
															{group.map((model) => (
																<PopoverItem
																	key={model.model}
																	active={value === model.model}
																	onClick={() => choose(model.model)}
																>
																	<span className="min-w-0 flex-1 break-words">
																		<span className="block font-medium">{model.display_name}</span>
																		<span className="block text-[var(--text-muted)]">
																			{t("settings.researchModelPrice", {
																				input: model.input_per_m.toFixed(2),
																				output: model.output_per_m.toFixed(2),
																			})}
																		</span>
																	</span>
																	{value === model.model && <Check aria-hidden />}
																</PopoverItem>
															))}
														</div>
													)
												);
											})}
										</>
									)}
								</div>
							</>
						)}
					</Async>
				</ModalContent>
			</Modal>
		</div>
	);
}
